import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

// CSS variables -----------------------------
const CardWrapper = styled(Link)`
  --card-height: 270px;
  --card-width: 180px;
  
  width: var(--card-width);
  height: calc(var(--card-height) + 50px); // Card height + info height
  margin-right: 15px;
  position: relative;
  cursor: pointer;
  display: block;
  flex-shrink: 0; // Prevent shrinking
  text-decoration: none;
  
  @media (max-width: 768px) {
    --card-height: 210px;
    --card-width: 140px;
  }
`;

const Card = styled.div`
  width: var(--card-width);
  height: var(--card-height);
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 0;
  perspective: 2500px;
`;

const Wrapper = styled.div`
  transition: all 0.5s;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: -1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  
  ${Card}:hover & {
    transform: perspective(900px) translateY(-5%) rotateX(25deg) translateZ(0);
    box-shadow: 2px 35px 32px -8px rgba(0, 0, 0, 0.75);
  }
  
  &::before,
  &::after {
    content: "";
    opacity: 0;
    width: 100%;
    height: 80px;
    transition: all 0.5s;
    position: absolute;
    left: 0;
  }
  
  &::before {
    top: 0;
    height: 100%;
    background-image: linear-gradient(
      to top,
      transparent 46%,
      rgba(12, 13, 19, 0.5) 68%,
      rgba(12, 13, 19) 97%
    );
  }
  
  &::after {
    bottom: 0;
    opacity: 1;
    background-image: linear-gradient(
      to bottom,
      transparent 46%,
      rgba(12, 13, 19, 0.5) 68%,
      rgba(12, 13, 19) 97%
    );
  }
  
  ${Card}:hover &::before,
  ${Card}:hover &::after {
    opacity: 1;
  }
  
  ${Card}:hover &::after {
    height: 120px;
  }
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Title = styled.div`
  width: 100%;
  transition: transform 0.5s;
  position: absolute;
  bottom: 20px;
  text-align: center;
  z-index: 2;
  font-size: 16px;
  font-weight: 600;
  padding: 0 10px;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.7);
  color: #fff;
  
  ${Card}:hover & {
    transform: translate3d(0%, -50px, 100px);
  }
`;

const Character = styled.img`
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: all 0.5s;
  position: absolute;
  z-index: -1;
  object-fit: cover;
  
  ${Card}:hover & {
    opacity: 1;
    transform: translate3d(0%, -30%, 100px);
  }
`;

const ContentType = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${props => props.$customColor || 'rgba(0, 0, 0, 0.7)'};
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  z-index: 2;
  border: 1px solid ${props => props.$customBorderColor || 'transparent'};
`;

const ContentIdentifier = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  background-color: ${props => props.$bgColor || 'rgba(255, 0, 0, 0.6)'};
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  border: 1px solid ${props => props.$borderColor || 'rgba(255, 0, 0, 0.3)'};
  z-index: 3;
  color: ${props => props.$textColor || 'white'};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 3px;
    font-size: 8px;
  }
`;

const CardInfo = styled.div`
  padding: 5px 0;
  height: 50px; // Fixed height for info section
`;

const InfoTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #fff;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  
  .year {
    margin-right: 10px;
  }
  
  .rating {
    display: flex;
    align-items: center;
    
    .star {
      color: #ffc107;
      margin-right: 3px;
    }
  }
`;

// Helper function to extract content identifiers from title
const extractIdentifier = (title) => {
  if (!title) return { cleanTitle: '', identifier: null };
  
  // Define regex patterns for the identifiers
  const patterns = [
    { regex: /\(UNCENSORED\)/i, id: 'UNCENSORED', 
      style: { 
        bgColor: 'rgba(255, 0, 0, 0.6)', 
        borderColor: 'rgba(255, 0, 0, 0.3)',
        customColor: 'rgba(0, 0, 0, 0.7)',
        customBorderColor: 'rgba(255, 70, 70, 0.3)'
      }
    },
    { regex: /\(HUNGAMA\)/i, id: 'HUNGAMA', 
      style: { 
        bgColor: 'rgba(255, 165, 0, 0.6)', 
        borderColor: 'rgba(255, 165, 0, 0.3)',
        customColor: 'rgba(0, 0, 0, 0.7)',
        customBorderColor: 'rgba(255, 165, 0, 0.3)',
        textColor: 'white'
      }
    },
    { regex: /\(CN\)/i, id: 'CN', 
      style: { 
        bgColor: 'rgba(0, 150, 255, 0.6)', 
        borderColor: 'rgba(0, 150, 255, 0.3)',
        customColor: 'rgba(0, 0, 0, 0.7)',
        customBorderColor: 'rgba(0, 150, 255, 0.3)'
      }
    }
  ];
  
  let cleanTitle = title;
  let foundIdentifier = null;
  
  // Check for each pattern
  for (const pattern of patterns) {
    if (pattern.regex.test(title)) {
      // Remove the identifier from the title
      cleanTitle = title.replace(pattern.regex, '').trim();
      foundIdentifier = {
        id: pattern.id,
        style: pattern.style
      };
      break; // Stop after finding the first match
    }
  }
  
  return { cleanTitle, identifier: foundIdentifier };
};

const HoverCard = ({ item, type }) => {
  if (!item) return null;
  
  // Extract content identifier from title
  const { cleanTitle, identifier } = extractIdentifier(item.title);
  
  // Use the type prop
  const contentType = type || item.type || (item.seasons ? 'show' : 'movie');
  // Use singular form to match App.js routes
  const itemId = item._id || item.id; // Use either _id or id
  const linkPath = `/${contentType === 'movie' ? 'movie' : 'show'}/${itemId}`;
  
  // year based on content type
  const year = item.releaseYear || item.year || item.startYear || '';
  const rating = item.rating ? (item.rating.toFixed(1) || 'N/A') : 'N/A';

  // Custom styling for content type based on identifier
  let contentTypeStyle = {};
  if (identifier) {
    contentTypeStyle = {
      $customColor: identifier.style.customColor,
      $customBorderColor: identifier.style.customBorderColor
    };
  }
  
  return (
    <CardWrapper to={linkPath}>
      <Card>
        <Wrapper>
          <CoverImage src={item.image} alt={cleanTitle} />
        </Wrapper>
        <Title>{cleanTitle}</Title>
        {item.hoverImage && (
          <Character src={item.hoverImage} alt={`${cleanTitle} character`} />
        )}
        <ContentType {...contentTypeStyle}>{contentType}</ContentType>
        
        {identifier && (
          <ContentIdentifier 
            $bgColor={identifier.style.bgColor}
            $borderColor={identifier.style.borderColor}
            $textColor={identifier.style.textColor}
          >
            {identifier.id === 'UNCENSORED' && <FontAwesomeIcon icon={faExclamationTriangle} />}
            {identifier.id}
          </ContentIdentifier>
        )}
      </Card>
      <CardInfo>
        <InfoTitle>{cleanTitle}</InfoTitle>
        <MetaInfo>
          {year && <span className="year">{year}</span>}
          {rating !== 'N/A' && (
            <span className="rating">
              <span className="star">★</span>
              {rating}
            </span>
          )}
        </MetaInfo>
      </CardInfo>
    </CardWrapper>
  );
};

export default HoverCard;